import { useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

import { useAuth } from '../auth/authContext'

// What the person sees for each way a login can fail (see LoginError in lib/api).
// The server's own wording is shown as the detail wherever it sent some.
const ERROR_TITLES = {
  invalid: 'Sign-in failed',
  validation: 'Missing details',
  throttled: 'Too many attempts',
  server: 'Server error',
  network: 'Can’t reach the server',
  timeout: 'The server isn’t responding',
  config: 'Server not configured',
  unknown: 'Something went wrong',
}

const ERROR_HINTS = {
  network: 'Check that the server is running and that you are on the right network, then try again.',
  timeout: 'Try again in a moment.',
  server: 'This is a problem on the server, not with your details. Try again shortly.',
  config: 'Set REACT_APP_TALLY_WS_URL (or REACT_APP_API_BASE_URL) in .env.local and restart the dev server.',
}

export default function LoginPage({ isDark, onToggleMode }) {
  const { login, expired } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const passwordRef = useRef(null)

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (submitting) return

    const problems = {}
    if (!email.trim()) problems.email = 'Enter your email.'
    if (!password) problems.password = 'Enter your password.'
    setFieldErrors(problems)
    if (Object.keys(problems).length > 0) return

    setError(null)
    setSubmitting(true)
    try {
      // On success the session changes and the router takes you to the dashboard.
      await login({ email_id: email.trim(), password })
    } catch (caught) {
      setError({ kind: caught?.kind ?? 'unknown', message: caught?.message ?? '' })
      // A wrong password is the one failure where the field is worth clearing.
      if (caught?.kind === 'invalid') {
        setPassword('')
        passwordRef.current?.focus()
      }
      setSubmitting(false)
    }
  }

  const hint = error ? ERROR_HINTS[error.kind] : null

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: '24px 16px',
        bgcolor: 'custom.paper',
        position: 'relative',
      }}
    >
      <Tooltip title={isDark ? 'Switch to light' : 'Switch to dark'}>
        <IconButton
          onClick={onToggleMode}
          aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
          sx={{
            position: 'absolute',
            top: 14,
            right: 16,
            width: 30,
            height: 30,
            borderRadius: '6px',
            color: 'custom.ink2',
            '&:hover': { bgcolor: 'custom.chip', color: 'custom.ink' },
          }}
        >
          {isDark ? <LightModeIcon sx={{ fontSize: 18 }} /> : <DarkModeIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          width: '100%',
          maxWidth: 380,
          bgcolor: 'custom.surface',
          border: '1px solid',
          borderColor: 'custom.line',
          borderRadius: '10px',
          p: '28px 26px 26px',
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: '8px', mb: '22px' }}>
          <MonitorHeartIcon sx={{ fontSize: 20, color: 'custom.up' }} />
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, letterSpacing: '-0.01em' }}>
            Tally Pingdesk
          </Typography>
        </Stack>

        <Typography component="h1" sx={{ fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.025em' }}>
          Sign in
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: 'custom.ink2', mt: '4px', mb: '20px' }}>
          Use your account to open the Tally Ping Monitor.
        </Typography>

        {expired && !error && (
          <Alert severity="info" sx={{ mb: '16px', fontSize: '0.78rem' }}>
            Your session expired. Please sign in again.
          </Alert>
        )}

        {error && (
          <Alert severity="error" role="alert" sx={{ mb: '16px', fontSize: '0.78rem' }}>
            <AlertTitle sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
              {ERROR_TITLES[error.kind] ?? ERROR_TITLES.unknown}
            </AlertTitle>
            {error.message}
            {hint && <Box sx={{ mt: '4px', color: 'custom.ink2' }}>{hint}</Box>}
          </Alert>
        )}

        <Stack sx={{ gap: '14px' }}>
          <TextField
            label="Email"
            name="email_id"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email}
            autoComplete="username"
            autoFocus
            fullWidth
            size="small"
            disabled={submitting}
          />

          <TextField
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password}
            autoComplete="current-password"
            fullWidth
            size="small"
            disabled={submitting}
            inputRef={passwordRef}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((shown) => !shown)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      edge="end"
                      size="small"
                      disabled={submitting}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <VisibilityIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={submitting}
            sx={{ mt: '6px', height: 38 }}
          >
            {submitting ? <CircularProgress size={18} color="inherit" aria-label="Signing in" /> : 'Sign in'}
          </Button>
        </Stack>
      </Box>
    </Box>
  )
}
